import { useState, useEffect, useRef, useCallback } from 'react';
import { cloudFunctionV2 } from '../functions';

// Recent-window lead fetch used by the LeadsActivityRail.
//
// Previously this was an SDK `onSnapshot` against the leads collection
// pulling whole documents — fast in theory (live push), catastrophic in
// practice (10–50 MB payloads on busy companies, 20+ second blocking load
// before first paint). Replaced with a one-shot cloud-function call using
// the existing getOrderedDocs endpoint + a slim server-side projection.
// Refreshes every REFRESH_INTERVAL_MS so new arrivals still show up
// within a reasonable beat without paying the live-subscription tax.
//
// Trade-off accepted: a new lead can take up to 30s to appear in the
// Recent Events feed (vs ~1s under the live snapshot). The feed is a
// morning-glance log, not a real-time monitor. The "Processing now"
// section uses useProcessingLeads (onSnapshot) for immediate updates.

const WINDOW_LIMIT = 200;
const REFRESH_INTERVAL_MS = 30 * 1000;

// Fields the activity rail actually reads (deriveLeadEvents +
// resolveDisposition + the Processing-now ProcessingRow). Keep this in
// sync with src/lib/leadEvents.js and src/components/LeadsActivityRail.js
// — adding a new field to the rail without updating this list will show
// it as undefined.
const RAIL_FIELDS = [
    'leadId',
    'propertyId',
    'createdAt',
    'updatedAt',
    'address.streetAddress',
    'address.city',
    'address.state',
    'currentAskingPrice',
    'currentSender',
    'originalSender',
    'source',
    'originSource',
    'sourceMetadata',
    // resolveLeadSourceProfile (src/lib/leadSource.js) keys the source icon off
    // `source` / `originSource` (above) only — the lead's true origin. GHL/FUB/
    // Salesforce icons appear only for leads that originated from that CRM's
    // webhook (source === 'ghl' | 'fub' | 'salesforce'). CRM linkage artifacts
    // (crmContactUrl / *WriteBack) are deliberately NOT projected here: they'd
    // be a reverse-lookup match, not the origin, and must not drive the icon.
    'status',
    'processingStage',
    // The pipeline's specific failure reason for status==='failed' leads —
    // surfaced as the precise "Result" in the rail's detail modal (DISCARDED).
    'errorMessage',
    'isArchived',
    'archiveRestoredAt',
    // smsWriteBack.authSource distinguishes a forwarded personal text
    // ('user-forward') from a company Twilio number ('company-twilio') — drives
    // the Messages vs Twilio source icon (resolveLeadSourceProfile).
    'smsWriteBack',
    'triage.priority_tier',
    'underwriting.arv',
    'underwriting.estimatedProfit',
    'underwriting.repairCost',
    'underwriting.arvSkipReason',
    'underwriting.repairSkipReason',
    'underwriting.repairCostEstimated',
    'dedupHits',
    'leadType'
];

// Slim projection for filtered-out leads (data.filtered === true). These are
// inbound deals dropped during ingestion — they were never enriched, so most
// have no address/propertyId. We read just enough to render a reason chip +
// source icon in the rail feed. See functions/propertyIngestionV2 +
// unipileEmailWorker.js for where filterStage/filterReasons are written.
const FILTERED_RAIL_FIELDS = [
    'leadId',
    'propertyId',
    'createdAt',
    'filtered',
    'filterStage',
    'filterReasons',
    // Geometry-free snapshot written by buildFilterDetails (post-enrichment
    // gate): the observed property values + the buy-box thresholds that
    // rejected them. describeFilterReasons (src/lib/leadEvents.js) uses this
    // to render the human-readable accordion in the filtered tab — without it
    // every reason body silently degrades to a no-detail fallback. Capped at
    // ~hundreds of strings (states/counties/zips/polygon names) so payload
    // impact stays trivial.
    'filterDetails',
    // Specific failure text (e.g. "Could not identify valid address from
    // input") — preferred over the generic filterReasons label in the modal.
    'errorMessage',
    'partialStructuredData',
    // The credit ledger for this lead, written by functions/utils/chargeLeadCredit.js.
    // Drives the rail's per-row "cost a credit" marker (resolveLeadCreditCharge in
    // src/lib/leadEvents.js). Both are needed: `creditChargedFor` is the current
    // per-analysis map, `creditCharged` the legacy single-credit boolean that seeds
    // ARV for pre-migration leads. Projecting only the map would silently read every
    // legacy paid lead back as free.
    'creditChargedFor',
    'creditCharged',
    // Separates the two zero-balance outcomes: never charged, vs charged then
    // refunded because the analysis didn't deliver. The refunded case is the common
    // one among unpaid rows, so without this the rail tells the wrong story about
    // most of the unpaid archives it shows.
    'creditRefundedAt',
    'address.streetAddress',
    // City/state/zip drive the geography reason's "Property is in {city,
    // state, zip}" line. address.state also falls back to filterDetails
    // .observed.state for older leads predating filterDetails.
    'address.city',
    'address.state',
    'address.zipcode',
    'currentAskingPrice',
    'source',
    'originSource',
    'sourceMetadata',
    'smsWriteBack'
];

// Shared windowed-leads fetcher. `extraParams` lets a caller add where-clauses
// (e.g. the filtered-leads view sets data.filtered + showArchived). Pulling the
// fetch/refresh machinery here keeps useLeadsWindow + useFilteredLeadsWindow in
// lock-step on the 24h window, polling cadence and mounted-guard behavior.
function useWindowedLeads(companyId, sinceMs, { fields, extraParams }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Mounted ref guards setState calls against late fetches that resolve
    // after the consumer unmounts or after the (companyId, sinceMs) tuple
    // changes (e.g. user navigates away mid-fetch).
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Refresh counter for the interval — bumping it forces the effect to
    // re-run without depending on Date.now() in the dep array.
    const [refreshTick, setRefreshTick] = useState(0);
    const inFlightRef = useRef(false);

    const fetchOnce = useCallback(async () => {
        if (!companyId || !sinceMs) return;
        if (inFlightRef.current) return; // skip overlapping fetches
        inFlightRef.current = true;
        try {
            const url = process.env.REACT_APP_GET_ORDERED_DOCS;
            if (!url) {
                if (mountedRef.current) {
                    setError(new Error('REACT_APP_GET_ORDERED_DOCS not configured'));
                    setLoading(false);
                }
                return;
            }
            const cutoffSeconds = Math.floor((Date.now() - sinceMs) / 1000);
            const res = await cloudFunctionV2(url, {
                collection: 'companies',
                subcollection: 'leads',
                document: companyId,
                field: 'data.createdAt',
                order: 'desc',
                limit: WINDOW_LIMIT,
                page: 1,
                rangeFilters: [
                    { field: 'data.createdAt._seconds', min: cutoffSeconds, max: null }
                ],
                fields,
                ...(extraParams || {})
            });
            if (!mountedRef.current) return;
            if (res.status !== 200) {
                setError(new Error(res.error || 'getOrderedDocs failed'));
                setLoading(false);
                return;
            }
            // getOrderedDocs returns rows shaped { data: {...} }; promote
            // leadId to the top level to match the previous SDK shape so
            // downstream consumers (deriveLeadEvents) don't need to change.
            const docs = (res.data || []).map((row) => ({
                leadId: row.data?.leadId,
                ...row
            }));
            setLeads(docs);
            setError(null);
            setLoading(false);
        } catch (err) {
            // cloudFunctionV2 normally swallows errors and returns { status, error },
            // but defensive: a network exception that escapes shouldn't trap
            // the consumer in a stuck loading state.
            if (mountedRef.current) {
                setError(err);
                setLoading(false);
            }
        } finally {
            inFlightRef.current = false;
        }
    }, [companyId, sinceMs, fields, extraParams]);

    useEffect(() => {
        if (!companyId || !sinceMs) {
            setLeads([]);
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        fetchOnce();

        // Periodic refresh while the consumer is mounted. Caller (the
        // rail) already pauses by passing companyId=null when collapsed,
        // so this never polls in the background.
        const id = setInterval(() => setRefreshTick((t) => t + 1), REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [companyId, sinceMs, fetchOnce]);

    useEffect(() => {
        if (refreshTick === 0) return;
        fetchOnce();
    }, [refreshTick, fetchOnce]);

    const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

    return { leads, loading, error, refresh };
}

// Recent arrivals (the rail's main feed). Pass companyId=null to pause.
export function useLeadsWindow(companyId, sinceMs) {
    return useWindowedLeads(companyId, sinceMs, { fields: RAIL_FIELDS });
}

// Filtered-out arrivals — leads dropped during ingestion. Separate query
// because filtered leads are archived and the main rail fetch excludes
// archived (getOrderedDocs.js splices isArchived === true when showArchived is
// falsy). Module-level extraParams keep the reference stable across renders.
const FILTERED_EXTRA_PARAMS = {
    whereField: 'data.filtered',
    whereValue: true,
    showArchived: true
};
export function useFilteredLeadsWindow(companyId, sinceMs) {
    return useWindowedLeads(companyId, sinceMs, {
        fields: FILTERED_RAIL_FIELDS,
        extraParams: FILTERED_EXTRA_PARAMS
    });
}
